import React, { useEffect } from "react";
import {
  Card,
  Typography,
  Descriptions,
  Row,
  Col,
  Divider,
  Tooltip,
  Form,
  Input,
  Button,
  message,
  Avatar,
} from "antd";
import { EditFilled, UserOutlined } from "@ant-design/icons";
import { useMutation, useQuery } from "@tanstack/react-query";
import { userProfile as fetchUserProfile } from "../../services/authService"; // Importing only once
import {
  businessModels,
  financialHealth,
  fundingStages,
  fundingTypes,
  industries,
  ipoStatus,
  legalStructures,
  marketPosition,
  queryKeys,
} from "../../constants";
import { useNavigate } from "react-router-dom";
import { updateUserProfile } from "../../services/scenarios";
import classes from "./Profile.module.scss";
import { updateProfilePicture } from "../../services/profile";
import UploadImage from "../../components/ImageUpload/UploadImage";
import { updateUserProfileAction } from "../../redux/action";
import { useDispatch } from "react-redux";

const dummyProfileData = {
  first_name: "",
  last_name: "",
  email: "",
  role: "",
  profile_picture: "",
  is_superuser: false,
  is_staff: false,
  is_active: false,
  company: null,
};

const { Title, Text } = Typography;

const Profile = () => {
  const dispatch = useDispatch();

  const navigate = useNavigate();
  const [editMode, setEditMode] = React.useState(false);
  const [form] = Form.useForm();

  const { data: profileData, refetch } = useQuery({
    queryKey: [queryKeys.userProfile],
    queryFn: fetchUserProfile,
  });

  const updateUserProfileMutation = useMutation({
    mutationFn: updateUserProfile,
    onSuccess: () => {
      message.success("Profile updated successfully!");
      setEditMode(false);
      refetch();
    },
    onError: (error) => {
      message.error(error.message);
    },
  });

  const updateProfilePictureMutation = useMutation({
    mutationFn: updateProfilePicture,
    onSuccess: () => {
      message.success("Profile picture updated successfully");
      refetch();
    },
    onError: (error) => {
      message.error(`Error: ${error.message}`);
    },
  });

  const handleEdit = () => {
    setEditMode(true);
  };

  const onFinish = (values) => {
    updateUserProfileMutation.mutate(values);
  };

  const handleUploadImage = (base64Image) => {
    updateProfilePictureMutation.mutate({ profile_picture: base64Image });
  };

  useEffect(() => {
    if (profileData) {
      dispatch(updateUserProfileAction(profileData.data));
    }
  }, [profileData, dispatch]);

  const {
    first_name,
    last_name,
    email,
    role,
    profile_picture,
    is_superuser,
    is_staff,
    is_active,
    company,
  } = profileData?.data ?? dummyProfileData;

  return (
    <div
      style={{
        padding: "20px",
      }}
    >
      {/* Profile Section */}
      <div className={classes.imageContainer}>
        <div style={{ minHeight: "102px" }}>
          <UploadImage
            profile_picture={profile_picture}
            handleUploadImage={handleUploadImage}
          />
        </div>
        <div>
          {/* Name and Role */}
          {editMode ? (
            <Form
              form={form}
              name="change_profile_details"
              onFinish={onFinish}
              layout="vertical"
              requiredMark="optional"
              initialValues={{
                first_name,
                last_name,
              }}
            >
              <Form.Item
                name="first_name"
                rules={[
                  {
                    required: true,
                    message: "Please input your first name!",
                  },
                ]}
              >
                <Input prefix={<UserOutlined />} placeholder="First name" />
              </Form.Item>
              <Form.Item
                name="last_name"
                rules={[
                  {
                    required: true,
                    message: "Please input your last name!",
                  },
                ]}
              >
                <Input prefix={<UserOutlined />} placeholder="Last name" />
              </Form.Item>
              <Form.Item>
                <Button type="primary" htmlType="submit">
                  Update
                </Button>
              </Form.Item>
            </Form>
          ) : (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "baseline",
                gap: "10px",
              }}
            >
              <Title
                level={3}
                style={{
                  fontSize: "24px",
                  fontWeight: "bold",
                  textTransform: "capitalize",
                }}
              >
                {first_name} {last_name}
              </Title>
              <EditFilled
                style={{
                  cursor: "pointer",
                  color: "#4096ff",
                }}
                onClick={handleEdit}
              />
            </div>
          )}
          <Text
            type="secondary"
            style={{ fontSize: "18px", textTransform: "capitalize" }}
          >
            {role}
          </Text>
        </div>
      </div>

      {/* Divider between profile and details */}
      <Divider style={{ margin: "20px 0" }} />

      {/* Information Section in two columns */}
      <Row gutter={[32, 32]}>
        {/* Personal Information */}
        <Col xs={24} md={12} sm={24}>
          <Card
            bordered={false}
            style={{
              height: "100%",
              boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
              borderRadius: "10px",
            }}
          >
            <Descriptions title="Personal Information" column={1}>
              <Descriptions.Item label="Email">{email}</Descriptions.Item>
              <Descriptions.Item label="Superuser">
                {is_superuser ? "Yes" : "No"}
              </Descriptions.Item>
              <Descriptions.Item label="Staff">
                {is_staff ? "Yes" : "No"}
              </Descriptions.Item>
              <Descriptions.Item label="Active">
                {is_active ? "Yes" : "No"}
              </Descriptions.Item>
            </Descriptions>
          </Card>
        </Col>

        {/* Company Information */}
        {company && (
          <Col xs={24} md={12} sm={24}>
            <Card
              bordered={false}
              style={{
                height: "100%",
                boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
                borderRadius: "10px",
                position: "relative",
              }}
            >
              <Tooltip title="Edit Company Details">
                <EditFilled
                  style={{
                    position: "absolute",
                    right: 20,
                    cursor: "pointer",
                    color: "#4096ff",
                  }}
                  onClick={() => navigate("/company-details?edit=true")}
                />
              </Tooltip>
              <Descriptions title="Company Information" column={1}>
                <Descriptions.Item label="Company Name">
                  <div className={classes.companyLogoInfo}>
                    <Avatar
                      size={24}
                      src={company.logo}
                      style={{ marginRight: 8 }}
                    />
                    {company.name ?? ""}
                  </div>
                </Descriptions.Item>
                <Descriptions.Item label="Industry">
                  {company.industry
                    ? industries.find((i) => i.value === company.industry)
                        ?.label || ""
                    : ""}
                </Descriptions.Item>
                <Descriptions.Item label="Industry Type">
                  {company.niche ?? ""}
                </Descriptions.Item>
                <Descriptions.Item label="Employee Count">
                  {company.employee_count ?? ""}
                </Descriptions.Item>
                <Descriptions.Item label="Geographic Presence">
                  {company.geographic_presence ?? ""}
                </Descriptions.Item>
                <Descriptions.Item label="Business Model">
                  {company.business_models
                    ?.map(
                      (model) =>
                        businessModels.find((bm) => bm.value === model)?.label
                    )
                    .join(", ") ?? ""}
                </Descriptions.Item>
                <Descriptions.Item label="Market Position">
                  {company.market_position
                    ? marketPosition.find(
                        (m) => m.value === company.market_position
                      )?.label ?? ""
                    : ""}
                </Descriptions.Item>
                <Descriptions.Item label="Financial Health">
                  {company.financial_health
                    ? financialHealth.find(
                        (m) => m.value === company.financial_health
                      )?.label ?? ""
                    : ""}
                </Descriptions.Item>
                {company.revenue_bracket && (
                  <Descriptions.Item label="Revenue Bracket">
                    {company.revenue_bracket ?? ""}
                  </Descriptions.Item>
                )}
                {company.annual_growth_rate && (
                  <Descriptions.Item label="Annual Growth">
                    {company.annual_growth_rate ?? ""}
                  </Descriptions.Item>
                )}
                {company.legal_structure && (
                  <Descriptions.Item label="Legal Structure">
                    {legalStructures.find(
                      (m) => m.value === company.legal_structure
                    )?.label ?? ""}
                  </Descriptions.Item>
                )}
                {company.ipo_status && (
                  <Descriptions.Item label="IPO Status">
                    {ipoStatus.find((i) => i.value === company.ipo_status)
                      ?.label ?? ""}
                  </Descriptions.Item>
                )}
                {company.funding_stage && (
                  <Descriptions.Item label="Funding Stage">
                    {fundingStages.find(
                      (i) => i.value === company.funding_stage
                    )?.label ?? ""}
                  </Descriptions.Item>
                )}
                {company.type_of_funding && (
                  <Descriptions.Item label="Funding Type">
                    {fundingTypes.find(
                      (i) => i.value === company.type_of_funding
                    )?.label ?? ""}
                  </Descriptions.Item>
                )}
              </Descriptions>
            </Card>
          </Col>
        )}
      </Row>
    </div>
  );
};

export default Profile;
